import { Pill, ShieldAlert, AlertTriangle, Utensils, FileText } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import type { PetFormData } from "@/pages/CadastrarPet";

interface Props {
  formData: PetFormData;
  updateField: <K extends keyof PetFormData>(field: K, value: PetFormData[K]) => void;
}

const SaudeCuidados = ({ formData, updateField }: Props) => {
  return (
    <div className="space-y-6">
      <Card className="card-shadow">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-lg font-bold">
            <div className="w-9 h-9 rounded-lg gradient-primary flex items-center justify-center">
              <Pill className="w-5 h-5 text-primary-foreground" />
            </div>
            Medicação
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 rounded-xl bg-muted/50">
            <Label htmlFor="toma_medicacao" className="text-sm font-semibold cursor-pointer">
              Toma medicação?
            </Label>
            <Switch
              id="toma_medicacao"
              checked={formData.toma_medicacao}
              onCheckedChange={(v) => updateField("toma_medicacao", v)}
            />
          </div>

          {formData.toma_medicacao && (
            <div className="grid gap-4">
              <div>
                <Label htmlFor="medicacao" className="text-sm font-semibold">Nome da Medicação</Label>
                <Input
                  id="medicacao"
                  value={formData.medicacao}
                  onChange={(e) => updateField("medicacao", e.target.value)}
                  placeholder="Ex: Rimadyl, Prednisolona..."
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="obs_medicacoes" className="text-sm font-semibold">Obs. sobre a medicação</Label>
                <Textarea
                  id="obs_medicacoes"
                  value={formData.obs_medicacoes}
                  onChange={(e) => updateField("obs_medicacoes", e.target.value)}
                  placeholder="Dosagem, horários, instruções especiais..."
                  rows={3}
                  className="mt-1"
                />
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="card-shadow">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-lg font-bold">
            <div className="w-9 h-9 rounded-lg gradient-coral flex items-center justify-center">
              <ShieldAlert className="w-5 h-5 text-secondary-foreground" />
            </div>
            Deficiência
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 rounded-xl bg-muted/50">
            <Label htmlFor="possui_deficiencia" className="text-sm font-semibold cursor-pointer">
              Possui deficiência?
            </Label>
            <Switch
              id="possui_deficiencia"
              checked={formData.possui_deficiencia}
              onCheckedChange={(v) => updateField("possui_deficiencia", v)}
            />
          </div>

          {formData.possui_deficiencia && (
            <div>
              <Label htmlFor="tipo_deficiencia" className="text-sm font-semibold">Qual deficiência?</Label>
              <Input
                id="tipo_deficiencia"
                value={formData.tipo_deficiencia}
                onChange={(e) => updateField("tipo_deficiencia", e.target.value)}
                placeholder="Descreva a deficiência..."
                className="mt-1"
              />
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="card-shadow">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-lg font-bold">
            <div className="w-9 h-9 rounded-lg bg-destructive/10 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-destructive" />
            </div>
            Alergias
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 rounded-xl bg-muted/50">
            <Label htmlFor="tem_alergia" className="text-sm font-semibold cursor-pointer">
              Tem alergia?
            </Label>
            <Switch
              id="tem_alergia"
              checked={formData.tem_alergia}
              onCheckedChange={(v) => updateField("tem_alergia", v)}
            />
          </div>

          {formData.tem_alergia && (
            <div>
              <Label htmlFor="alergias" className="text-sm font-semibold">Quais alergias?</Label>
              <Textarea
                id="alergias"
                value={formData.alergias}
                onChange={(e) => updateField("alergias", e.target.value)}
                placeholder="Liste as alergias do pet..."
                rows={3}
                className="mt-1"
              />
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="card-shadow">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-lg font-bold">
            <div className="w-9 h-9 rounded-lg bg-accent/10 flex items-center justify-center">
              <Utensils className="w-5 h-5 text-coral" />
            </div>
            Alimentação
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 rounded-xl bg-muted/50">
            <Label htmlFor="alimentacao_especial" className="text-sm font-semibold cursor-pointer">
              Alimentação especial?
            </Label>
            <Switch
              id="alimentacao_especial"
              checked={formData.alimentacao_especial}
              onCheckedChange={(v) => updateField("alimentacao_especial", v)}
            />
          </div>

          {formData.alimentacao_especial && (
            <div>
              <Label htmlFor="descricao_alimentacao" className="text-sm font-semibold">Descrição da alimentação</Label>
              <Textarea
                id="descricao_alimentacao"
                value={formData.descricao_alimentacao}
                onChange={(e) => updateField("descricao_alimentacao", e.target.value)}
                placeholder="Descreva a alimentação especial..."
                rows={3}
                className="mt-1"
              />
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="card-shadow">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-lg font-bold">
            <div className="w-9 h-9 rounded-lg bg-muted flex items-center justify-center">
              <FileText className="w-5 h-5 text-muted-foreground" />
            </div>
            Histórico de Doenças
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Textarea
            id="doencas"
            value={formData.doencas}
            onChange={(e) => updateField("doencas", e.target.value)}
            placeholder="Descreva o histórico de doenças do pet..."
            rows={4}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default SaudeCuidados;